/**
 * The browser-side half of `/link-preview` — the paste-a-link path in
 * `PurchaseList.tsx` (WP 4.5). Same auth shape as `leadVenue.ts`'s
 * `authedFetch`: the Supabase session, not `?key=ARCHIVE_KEY` — see the
 * Worker's own `linkPreview()` in `src/inbox.ts` for why a Worker secret
 * cannot be the gate on a route the app calls from the browser.
 *
 * Every field null (bar `host`) covers both shapes rule 4 requires be
 * indistinguishable to a caller: a page that genuinely has no Open Graph or
 * JSON-LD to read, and one that answered 403 and was never retried. Deciding
 * "is there anything worth filling in" from that is `PurchaseList.tsx`'s job,
 * not this file's.
 */
import { supabase } from './supabase'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

export interface LinkPreview {
  title: string | null
  price_cents: number | null
  currency: string | null
  image_url: string | null
  host: string
}

export async function fetchLinkPreview(url: string): Promise<LinkPreview> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not signed in')

  const res = await fetch(`${WORKER_URL}/link-preview?url=${encodeURIComponent(url)}`, {
    headers: { authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`Link preview failed: ${res.status} ${await res.text()}`)
  return res.json()
}
